// services/lti.ts
//
// LTI 1.3 "Connect Account" landing flow (ORA-1046). The LMS launch sends the
// browser to `/lti/connect/<pendingLaunchId>`; the student connects their Ora
// account by completing a fresh sign-in while a server-side linking intent is
// open, then confirms. The `lti_launch` cookie (set by the backend's launch)
// is the browser binding: it rides on every call here, and on the sign-in
// calls, because axios and the Better Auth client send credentials.
import axios from 'axios';

import axiosInstance from './axiosCreator';
import { AUTH_REDIRECT_STORAGE_KEY } from './authNavigation';
import type { AssignmentType } from '../types/Assignment';

export interface LtiLaunchAssignment {
  id: string;
  type: AssignmentType;
  title: string;
  cohortId: string;
}

/**
 * What the LMS link opens (ORA-1076): a cohort assignment, a course module,
 * or one assessment step of a course.
 */
export type LtiLaunchTargetKind =
  'COHORT_ASSIGNMENT' | 'COURSE_MODULE' | 'COURSE_ASSESSMENT_STEP';

export interface LtiLaunchTarget {
  kind: LtiLaunchTargetKind;
  cohortAssignment?: LtiLaunchAssignment | null;
  course?: { id: string; slug: string; title: string };
  module?: { id: string; ord: number; slug: string; title: string };
  /**
   * COURSE_MODULE: signed in, the student's first incomplete step, or null
   * when the module is complete (or has no steps); signed out, the module's
   * first step. COURSE_ASSESSMENT_STEP: the step itself.
   */
  step?: { id: string; kind: string; title: string } | null;
}

/** The deployment's instance ('default' when it has none), for branding. */
export interface LtiLaunchInstance {
  slug: string;
  displayName: string;
  logoUrl: string | null;
  wordmarkUrl: string | null;
  capabilities: Record<string, unknown>;
}

export interface LtiLaunchStatus {
  isExpired: boolean;
  isConsumed: boolean;
  isRefused: boolean;
  isSetupLaunch: boolean;
  isLearner: boolean;
  isInstructor: boolean;
  isConnected: boolean;
  canStartLinking: boolean;
}

export interface LtiLaunchDescription {
  pendingLaunchId: string;
  institutionName: string;
  courseLabel: string;
  assignment: LtiLaunchAssignment | null;
  /** The signed-in account, its email masked by the server; null signed out. */
  account: { email: string } | null;
  refusalReason: string | null;
  status: LtiLaunchStatus;
  /**
   * Null for a refused or setup launch. Absent from a backend before
   * ORA-1076: read as the cohort assignment.
   */
  target?: LtiLaunchTarget | null;
  instance?: LtiLaunchInstance;
  /**
   * The signed-in connected learner's hold on a course target: enrolled when
   * the course is theirs and they hold a live seat. Null for a cohort target
   * or signed out; absent from a backend before be #1112.
   */
  enrolment?: LtiLaunchEnrolment | null;
}

export interface LtiLaunchEnrolment {
  enrolled: boolean;
  reason?:
    'no_live_seat' | 'cohort_course_mismatch' | 'not_enrolled' | (string & {});
}

export interface LtiLinkingIntent {
  intentId: string;
  expiresAt: string;
}

/** The course a course-target confirm enrolled the student in (or tried to). */
export interface LtiConfirmationCourse {
  id: string;
  slug: string;
  enrolled: boolean;
  /**
   * Why the confirm did not enrol the student. Absent with `enrolled: false`
   * when the student already holds the course (a relaunch).
   */
  reason?: 'no_live_seat' | 'cohort_course_mismatch' | (string & {});
  /** The launched course became the student's current course. */
  switched?: boolean;
}

export interface LtiConfirmation {
  /** Null for a course target. */
  assignment: LtiLaunchAssignment | null;
  connectionId: string;
  /** Null for a cohort-assignment target. */
  course: LtiConfirmationCourse | null;
}

interface LtiSuccess<T> {
  success: true;
  data: T;
}

/**
 * A refused `/lti` request. `code` is the backend's reason
 * (`{ success:false, error, code }`), or null when the request never got an
 * answer (offline, a 5xx without a body).
 */
export class LtiRequestError extends Error {
  code: string | null;
  status: number | null;

  constructor(code: string | null, status: number | null) {
    super(code ?? 'LTI request failed');
    this.name = 'LtiRequestError';
    this.code = code;
    this.status = status;
  }
}

const toLtiRequestError = (error: unknown): LtiRequestError => {
  if (!axios.isAxiosError(error)) return new LtiRequestError(null, null);

  const status = error.response?.status ?? null;
  const code = (error.response?.data as { code?: unknown } | undefined)?.code;
  return new LtiRequestError(typeof code === 'string' ? code : null, status);
};

const ltiPath = (pendingLaunchId: string, suffix = '') =>
  `/lti/connect/${encodeURIComponent(pendingLaunchId)}${suffix}`;

/** What the landing page shows for a launch. Public: no session needed. */
export const describeLtiLaunch = async (
  pendingLaunchId: string
): Promise<LtiLaunchDescription> => {
  try {
    const response = await axiosInstance.get<LtiSuccess<LtiLaunchDescription>>(
      ltiPath(pendingLaunchId)
    );

    return response.data.data;
  } catch (error) {
    throw toLtiRequestError(error);
  }
};

/** Opens a linking intent: the next sign-in in this browser proves identity. */
export const startLtiLinkingIntent = async (
  pendingLaunchId: string
): Promise<LtiLinkingIntent> => {
  try {
    const response = await axiosInstance.post<LtiSuccess<LtiLinkingIntent>>(
      ltiPath(pendingLaunchId, '/intent')
    );

    return response.data.data;
  } catch (error) {
    throw toLtiRequestError(error);
  }
};

/** Connects the signed-in account to the LMS identity of the launch. */
export const confirmLtiLinking = async (
  pendingLaunchId: string,
  intentId: string
): Promise<LtiConfirmation> => {
  try {
    const response = await axiosInstance.post<LtiSuccess<LtiConfirmation>>(
      ltiPath(pendingLaunchId, '/confirm'),
      { intentId }
    );

    return response.data.data;
  } catch (error) {
    throw toLtiRequestError(error);
  }
};

// --- The pending intent ----------------------------------------------------
//
// Kept in sessionStorage (this tab only) between starting the intent on the
// landing page and the sign-in on /auth, so the sign-in calls can name it.

export const LTI_INTENT_STORAGE_KEY = 'ora-lti-intent';

/** Header the backend reads on the email, passkey and 2FA sign-in calls. */
export const LTI_INTENT_HEADER = 'x-lti-intent';

/** What the landing page stores: the launch plus the server's intent. */
export interface LtiIntentToStore {
  pendingLaunchId: string;
  intentId: string;
  /** The server's absolute expiry, kept for reference only. */
  expiresAt: string;
}

export interface StoredLtiIntent extends LtiIntentToStore {
  /** When this tab stops using the intent, on this device's clock (ms). */
  localExpiresAt: number;
}

/**
 * The local lifetime of an intent is bounded so that a client clock that
 * runs fast or slow against the server's can neither expire the intent at
 * once nor keep it for long. The server stays the authority at confirm time.
 */
export const LTI_INTENT_MIN_LOCAL_TTL_MS = 60_000;
export const LTI_INTENT_MAX_LOCAL_TTL_MS = 15 * 60_000;

const localExpiryFor = (expiresAt: string, now: number): number => {
  const serverTtlMs = Date.parse(expiresAt) - now;

  // An unparseable expiry gets the shortest lifetime; the server decides.
  if (Number.isNaN(serverTtlMs)) return now + LTI_INTENT_MIN_LOCAL_TTL_MS;

  return (
    now +
    Math.min(
      Math.max(serverTtlMs, LTI_INTENT_MIN_LOCAL_TTL_MS),
      LTI_INTENT_MAX_LOCAL_TTL_MS
    )
  );
};

const isStoredLtiIntent = (value: unknown): value is StoredLtiIntent => {
  const candidate = value as Partial<StoredLtiIntent> | null;
  return (
    typeof candidate?.pendingLaunchId === 'string' &&
    typeof candidate.intentId === 'string' &&
    typeof candidate.expiresAt === 'string' &&
    typeof candidate.localExpiresAt === 'number' &&
    Number.isFinite(candidate.localExpiresAt)
  );
};

const isExpired = (intent: StoredLtiIntent, now: number): boolean =>
  intent.localExpiresAt <= now;

export const clearLtiIntent = (): void => {
  try {
    sessionStorage.removeItem(LTI_INTENT_STORAGE_KEY);
  } catch {
    // Storage unavailable: nothing was stored either.
  }
};

/**
 * Stores the intent just received from the server, with a local expiry
 * computed now from the server's remaining lifetime (see `localExpiryFor`).
 */
export const writeLtiIntent = (
  intent: LtiIntentToStore,
  now = Date.now()
): void => {
  const stored: StoredLtiIntent = {
    ...intent,
    localExpiresAt: localExpiryFor(intent.expiresAt, now),
  };

  try {
    sessionStorage.setItem(LTI_INTENT_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage unavailable: the sign-in then carries no intent and the
    // confirm step answers `proof_required`, which restarts the flow.
  }
};

/**
 * The stored, unexpired intent, or null. An expired or unreadable entry is
 * cleared.
 */
export const readLtiIntent = (now = Date.now()): StoredLtiIntent | null => {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(LTI_INTENT_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = null;
  }

  if (!isStoredLtiIntent(parsed) || isExpired(parsed, now)) {
    clearLtiIntent();
    return null;
  }

  return parsed;
};

/** The stored intent, only when it belongs to this launch. */
export const readLtiIntentForLaunch = (
  pendingLaunchId: string
): StoredLtiIntent | null => {
  const intent = readLtiIntent();
  return intent?.pendingLaunchId === pendingLaunchId ? intent : null;
};

const LTI_CONNECT_PATH = /^\/lti\/connect\/([^/?#]+)\/?$/;

/** The launch id of a `/lti/connect/<id>` path, or null for any other path. */
export const pendingLaunchIdFromPath = (path: string | null): string | null => {
  if (!path) return null;
  try {
    const { pathname } = new URL(path, window.location.origin);
    const match = LTI_CONNECT_PATH.exec(pathname);
    if (!match || match[1] === 'error') return null;
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
};

/** The pages that complete an LTI linking sign-in. */
const LTI_SIGN_IN_PATHS = new Set(['/auth', '/auth/2fa']);

const isOnLtiSignInPage = (): boolean =>
  LTI_SIGN_IN_PATHS.has(window.location.pathname);

/**
 * Where this sign-in returns to: the auth page's `?redirect=`, else the one
 * it parked in sessionStorage (the 2FA page carries none). Only read on the
 * auth and 2FA pages (see `isOnLtiSignInPage`).
 */
const readSignInReturnPath = (): string | null => {
  const redirectParam = new URLSearchParams(window.location.search).get(
    'redirect'
  );

  if (redirectParam) return redirectParam;

  try {
    return sessionStorage.getItem(AUTH_REDIRECT_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * The intent id a sign-in should carry, or null. Only a sign-in on the auth
 * or 2FA page can carry one: a sign-in elsewhere (the webinar modal, the
 * unlock page) never does, and leaves the intent in place for the student to
 * come back to. There, only a stored, unexpired intent whose launch is the
 * page this sign-in returns to counts; any other stored intent is stale (the
 * student went elsewhere) and is cleared, so an ordinary sign-in never
 * carries one.
 */
export const getLtiIntentIdForSignIn = (): string | null => {
  if (!isOnLtiSignInPage()) return null;

  const intent = readLtiIntent();
  if (!intent) return null;

  if (
    pendingLaunchIdFromPath(readSignInReturnPath()) !== intent.pendingLaunchId
  ) {
    clearLtiIntent();
    return null;
  }

  return intent.intentId;
};

/**
 * `fetchOptions` for a Better Auth sign-in call: the intent header while an
 * intent applies, otherwise undefined (callers then send no `fetchOptions`
 * at all, exactly as before ORA-1046).
 */
export const getLtiIntentFetchOptions = ():
  { headers: Record<string, string> } | undefined => {
  const intentId = getLtiIntentIdForSignIn();
  return intentId ? { headers: { [LTI_INTENT_HEADER]: intentId } } : undefined;
};
